import React, { useEffect, useMemo } from "react";
import cs from "classnames";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { useWorkplaceRequests } from "../../../api/graphql/useWorkplaceRequests";
import { PageSidebar } from "../PageSidebar/PageSidebar";
import { PageSidebarHeader } from "../PageSidebar/PageSidebarHeader/PageSidebarHeader";
import { Icon } from "../Icon/Icon";
import { HandleLoadingState } from "../HandleLoadingState/HandleLoadingState";
import { WorkplacesTreeLoader } from "../../../views/Manage/Workplaces/WorkplacesTree/WorkplacesTreeLoader/WorkplacesTreeLoader";
import { ApolloErrorGuard } from "../ApolloErrorGuard/ApolloErrorGuard";
import { WorkplaceFields } from "../../../api/graphql/workplaces";

interface Props {
  message: string;
  closePicker: () => void;
  description: string;
  sidebarClassName?: string;
  showCloseButton?: boolean;
  children: (data: {
    rootWorkplaces: WorkplaceFields[][];
    loading: boolean;
    error: any;
  }) => React.ReactNode;
  footer?: React.ReactNode;
  title?: string;
}

export const WorkspacePicker = ({
  message,
  description,
  closePicker,
  sidebarClassName,
  showCloseButton = true,
  children,
  footer,
  title,
}: Props) => {
  const { user } = useAuthContext();
  const { getRootWorkplaces } = useWorkplaceRequests();
  const [request, { loading, error, data }] = getRootWorkplaces;

  // Stable between renders, so children can memoize on it
  const rootWorkplaces = useMemo(
    () =>
      Object.values(data?.queryTenant[0] || {}).filter(
        (item): item is WorkplaceFields[] => Array.isArray(item)
      ),
    [data]
  );

  useEffect(() => {
    request({ variables: { tenantId: user?.customerid || "" } });
  }, []);

  return (
    <PageSidebar
      className={cs(
        "PageSidebar--default",
        "PageSidebar--toggled",
        "PageSidebar--Custom",
        "PageSidebar--Wayfinder",
        sidebarClassName
      )}
      showOnInitialization={false}
      showOnDesktop={false}
      hideToggler
    >
      <PageSidebarHeader
        title={title || "Workspace picker"}
        message={message}
      />

      {showCloseButton && (
        <div onClick={closePicker} className="PageSidebar--Wayfinder--closer">
          <Icon icon="close-icon" />
        </div>
      )}

      <div
        className="
      PageSidebar--Wayfinder--desc
      "
      >
        {description}
      </div>

      <HandleLoadingState
        loading={loading}
        loadingPlaceholder={<WorkplacesTreeLoader />}
      >
        <ApolloErrorGuard
          error={error}
          errorComponent={<p>Data couldn't be loaded.</p>}
        >
          <div className="PageSidebar--Wayfinder--items">
            {children({ rootWorkplaces, loading, error })}
          </div>
        </ApolloErrorGuard>
      </HandleLoadingState>
      {footer && <div className="WorkspacePicker__footer">{footer}</div>}
    </PageSidebar>
  );
};
